import React, { useEffect, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { SlidersHorizontal, Save, RotateCcw } from "lucide-react";

const FIELDS = [
  { key: "fail_threshold", label: "Tentativi falliti", hint: "N° di accessi falliti che fanno scattare il blocco", suffix: "tentativi", min: 3, max: 1000 },
  { key: "window_minutes", label: "Finestra di tempo", hint: "Entro quanti minuti devono avvenire i tentativi", suffix: "minuti", min: 1, max: 1440 },
  { key: "temp_block_hours", label: "Durata blocco temporaneo", hint: "Quante ore dura il primo blocco automatico", suffix: "ore", min: 1, max: 8760 },
  { key: "permanent_after", label: "Blocco permanente dopo", hint: "N° di blocchi ripetuti (recidive) prima del blocco definitivo", suffix: "recidive", min: 1, max: 100 },
];

const inputCls = "w-24 border border-gray-300 rounded-sm px-3 py-2 text-[14px] font-bold text-[#16345c] outline-none focus:border-[#16345c] bg-white";

export const SecurityThresholds = () => {
  const [form, setForm] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/security/settings")
      .then((r) => {
        const { defaults: d, updated_at, updated_by, ...vals } = r.data || {};
        setForm(vals);
        setDefaults(d);
        setMeta({ updated_at, updated_by });
      })
      .catch(() => toast.error("Errore nel caricamento delle soglie"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const setVal = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = () => {
    setSaving(true);
    api.put("/security/settings", form)
      .then((r) => {
        const { defaults: d, updated_at, updated_by, ...vals } = r.data || {};
        setForm(vals);
        setMeta({ updated_at, updated_by });
        toast.success("Soglie di sicurezza aggiornate");
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Errore durante il salvataggio"))
      .finally(() => setSaving(false));
  };

  const resetDefaults = () => { if (defaults) setForm({ ...defaults }); };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

  if (loading || !form) {
    return <div data-testid="security-thresholds" className="bg-white border border-gray-200 rounded-lg p-4 mb-5 text-[13px] text-gray-400">Caricamento impostazioni…</div>;
  }

  return (
    <div data-testid="security-thresholds" className="bg-white border border-gray-200 rounded-lg p-4 mb-5">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-9 h-9 rounded-lg bg-[#16345c]/10 text-[#16345c] flex items-center justify-center"><SlidersHorizontal size={18} /></span>
        <div>
          <h3 className="text-[15px] font-black text-[#16345c] leading-tight">Impostazioni blocco automatico</h3>
          <p className="text-[12px] text-gray-500">Definisci quando il sistema blocca automaticamente un indirizzo IP.</p>
        </div>
      </div>

      <p className="text-[13px] text-gray-600 bg-[#f0f2f5] rounded-sm px-3 py-2 my-3">
        Blocca un IP dopo <b>{form.fail_threshold}</b> tentativi falliti in <b>{form.window_minutes}</b> minuti · blocco temporaneo di <b>{form.temp_block_hours}</b> ore · blocco <b>permanente</b> dopo <b>{form.permanent_after}</b> recidive.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {FIELDS.map((f) => (
          <div key={f.key} className="flex flex-col gap-1">
            <label className="text-[12px] font-bold text-gray-700">{f.label}</label>
            <div className="flex items-center gap-2">
              <input
                type="number" min={f.min} max={f.max}
                data-testid={`threshold-${f.key}`}
                value={form[f.key] ?? ""}
                onChange={(e) => setVal(f.key, e.target.value === "" ? "" : Number(e.target.value))}
                className={inputCls}
              />
              <span className="text-[12px] text-gray-500">{f.suffix}</span>
            </div>
            <span className="text-[11px] text-gray-400 leading-tight">{f.hint}</span>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mt-4 flex-wrap">
        <button onClick={save} disabled={saving} data-testid="threshold-save" className="flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2 rounded-sm transition-colors disabled:opacity-60">
          <Save size={15} /> {saving ? "Salvataggio…" : "Salva impostazioni"}
        </button>
        <button onClick={resetDefaults} disabled={saving} data-testid="threshold-reset" className="flex items-center gap-2 border border-gray-300 text-gray-600 hover:bg-gray-100 text-[13px] font-bold px-4 py-2 rounded-sm transition-colors disabled:opacity-60">
          <RotateCcw size={15} /> Valori consigliati
        </button>
        {meta.updated_at && (
          <span className="text-[11px] text-gray-400 ml-auto" data-testid="threshold-updated">Ultima modifica: {fmt(meta.updated_at)}{meta.updated_by ? ` · ${meta.updated_by}` : ""}</span>
        )}
      </div>
    </div>
  );
};
